import React, { useEffect, useState, useMemo } from 'react';
import Avatar from '../common/Avatar';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../common/Toast';
import { getWorkItemsForBoard, updateWorkItemsStatus } from '../../api/workItems';
import { WORK_ITEM_TYPE_LABELS, WORK_ITEM_TYPE_COLORS, WORK_ITEM_PRIORITY_META } from '../../constants/workItem';
import { getTextColorForBg } from '../../utils/textUtils';
import './WorkItemBoard.scss';

/**
 * WorkItemBoard — 工作項目看板
 * 全公司「父層」工作項目(不含子項),依狀態分欄。closed 預設收起。
 * 卡片可拖到別欄改狀態(optimistic + audit)。點卡片開 drawer。
 */
const WorkItemBoard = ({ companyId, companyUsers, statusOptions, onOpenItem }) => {
  const { profile } = useAuth();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const usersById = useMemo(() => {
    const m = new Map();
    (companyUsers || []).forEach((u) => m.set(u.id, u));
    return m;
  }, [companyUsers]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getWorkItemsForBoard({ companyId }).then(({ data }) => {
      if (!alive) return;
      setItems(data || []);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [companyId]);

  // 依狀態分欄(欄順序 = statusOptions;closed 收起不成欄)
  const columns = useMemo(() => {
    const cols = (statusOptions || []).filter((s) => s.value !== 'closed');
    const byStatus = {};
    cols.forEach((s) => { byStatus[s.value] = []; });
    items.forEach((it) => {
      if (byStatus[it.status]) byStatus[it.status].push(it);
    });
    return cols.map((s) => ({ ...s, items: byStatus[s.value] || [] }));
  }, [items, statusOptions]);

  // Owner = 最早 assigned_at
  const ownerOf = (it) => {
    const sorted = [...(it.assignees || [])].sort((a, b) => new Date(a.assigned_at || 0) - new Date(b.assigned_at || 0));
    return sorted[0]?.user_id || null;
  };

  const handleDrop = async (status) => {
    const id = dragId;
    setDragId(null);
    setOverCol(null);
    if (!id) return;
    const target = items.find((it) => it.id === id);
    if (!target || target.status === status) return;
    const prevStatus = target.status;
    // optimistic:先移到目標欄
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
    const { error } = await updateWorkItemsStatus([id], status, profile?.id);
    if (error) {
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status: prevStatus } : it)));
      toast.error('更新狀態失敗');
    }
  };

  if (loading) return <div className="wi-board-loading">載入中…</div>;

  return (
    <div className="wi-board">
      {columns.map((col) => (
        <div
          className={`wi-board-col ${overCol === col.value ? 'wi-board-col--over' : ''}`}
          key={col.value}
          onDragOver={(e) => { if (dragId) { e.preventDefault(); if (overCol !== col.value) setOverCol(col.value); } }}
          onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget) && overCol === col.value) setOverCol(null); }}
          onDrop={() => handleDrop(col.value)}
        >
          <div className="wi-board-col-head">
            <span className="wi-board-dot" style={{ background: col.color }} />
            <span className="wi-board-col-title">{col.label}</span>
            <span className="wi-board-col-count">{col.items.length}</span>
          </div>
          <div className="wi-board-col-body">
            {col.items.map((it) => {
              const tc = WORK_ITEM_TYPE_COLORS[it.type] || WORK_ITEM_TYPE_COLORS.task;
              const pm = WORK_ITEM_PRIORITY_META[it.priority];
              const oid = ownerOf(it);
              const owner = oid ? usersById.get(oid) : null;
              const projColor = it.projects?.color || '#667eea';
              return (
                <div
                  className={`wi-board-card ${dragId === it.id ? 'wi-board-card--dragging' : ''}`}
                  key={it.id}
                  draggable
                  onDragStart={(e) => { setDragId(it.id); e.dataTransfer.effectAllowed = 'move'; }}
                  onDragEnd={() => { setDragId(null); setOverCol(null); }}
                  onClick={() => onOpenItem?.(it)}
                >
                  <div className="wi-board-card-top">
                    <span className="wi-board-type" style={{ background: tc.bg, color: tc.fg }}>{WORK_ITEM_TYPE_LABELS[it.type] || it.type}</span>
                    <span className="wi-board-no">#{it.work_item_no}</span>
                    {pm && it.priority > 0 && <span className="wi-board-prio" style={{ background: pm.color, color: getTextColorForBg(pm.color) }}>{pm.label}</span>}
                  </div>
                  <div className="wi-board-card-title">{it.title}</div>
                  <div className="wi-board-card-foot">
                    <span className="wi-board-proj" style={{ background: projColor, color: getTextColorForBg(projColor) }}>{it.projects?.name || '專案'}</span>
                    {owner ? <Avatar avatarUrl={owner.avatar_url} name={owner.name} size="xsmall" /> : <span className="wi-board-noowner">—</span>}
                  </div>
                </div>
              );
            })}
            {col.items.length === 0 && <div className="wi-board-empty">—</div>}
          </div>
        </div>
      ))}
    </div>
  );
};

export default WorkItemBoard;
